/**
 * The reply-proposal text machinery for the Requests tab's scheduling connection.
 *
 * Split from InboundCard.tsx for that file's 500-line ceiling. Everything here is about ONE
 * question: given what is currently in the reply editor and which slots are ticked on the embedded
 * scheduling card, what should the reply say? The functions are pure (string in, string out) so the
 * swap rules — replace an earlier proposal, else replace the holding promise, else append or stay
 * out — are unit-testable without a contentEditable in sight.
 */
import type { ReactNode } from 'react';
import type { GradedSlot } from '../../types/taActionBoard';
import { fmtSlotRange, fmtSlotRangeDe } from './format';

/** The tools the scheduling connection gets back from the card — see InboundCard. */
export interface ReplyTools {
  /** "✉️ Propose in reply": replace the current proposal (or the promise), else append. */
  proposeSlots: (slots: GradedSlot[]) => void;
  /**
   * The tick-driven mirror: fired by the embedded slot overview on every selection change
   * (including its mount, with the pre-ticked suggestions), so the reply text always shows the
   * times that are ticked on the card. It rewrites ONLY text it can recognise — the proposal
   * block it wrote before, or the untouched holding promise; unticking everything puts the
   * promise back. A draft edited beyond recognition is left alone: mirroring must never fight
   * the recruiter for the keyboard.
   */
  syncProposalSlots: (slots: GradedSlot[]) => void;
}

/**
 * Renders the scheduling connection with the card's reply tools. Its own component (rather than
 * `schedulingConnection?.({ proposeSlots })` inline in InboundCard) so the render-prop call happens
 * in a render where no ref is in scope — react-hooks/refs cannot otherwise prove the editor ref
 * stays out of the render path.
 */
export function ConnectionSlot({
  render,
  tools,
}: {
  render: (tools: ReplyTools | undefined) => ReactNode;
  /** Absent when the card has NO editable draft — proposal controls must not write into a hidden editor. */
  tools?: ReplyTools;
}) {
  return <>{render(tools)}</>;
}

/**
 * The proposal block. Its FRAME (the two fixed sentences) and its slot format are shared with the
 * proxy's `inboundSlotProposal.proposalBlock`, which writes the same block into the draft on the
 * server since Sep 2026 — a test on each side pins the same fixture string, because
 * `existingProposal` below only recognises a block whose frame matches this one.
 */
export const proposalBlock = (slots: GradedSlot[], german: boolean) => {
  const lines = slots.map((sl) => `- ${german ? fmtSlotRangeDe(sl) : fmtSlotRange(sl)}`).join('<br>');
  return german
    ? `Passt einer dieser Termine bei dir?<br><br>${lines}<br><br>Schreib mir einfach kurz, welcher Termin passt, dann schicke ich dir die Einladung.`
    : `Would one of these times work for you?<br><br>${lines}<br><br>Just let me know which one works best and I will send the calendar invite.`;
};

/**
 * An earlier proposal, recognised by its own fixed frame, so re-proposing REPLACES the list
 * instead of stacking a second one under the first.
 */
const existingProposal = (current: string) =>
  [
    /Passt einer dieser Termine bei dir\?[\s\S]*?dann schicke ich dir die Einladung\./,
    /Would one of these times work for you\?[\s\S]*?I will send the calendar invite\./,
  ].find((re) => re.test(current));

/**
 * The proposal block currently in the text, as written — what the mirror treats as ITS OWN when the
 * server put it there (InboundCard seeds `lastWrittenProposal` with this). Null when there is none.
 */
export function proposalIn(current: string): string | null {
  const re = existingProposal(current);
  return re ? (current.match(re)?.[0] ?? null) : null;
}

/**
 * The holding promises a proposal replaces, verbatim from the proxy's HOLDING_LINES
 * (inboundReply.js). Drift is harmless: an unmatched promise means the block is appended below
 * it (manual propose) or nothing happens (tick sync) — wrong-looking, never wrong-sending.
 */
const PROMISES = [
  'Ich melde mich in Kürze mit ein paar Terminvorschlägen bei dir.',
  'I will come back to you shortly with a few times to choose from.',
  'Ich melde mich mit einem Terminvorschlag, der in dein Zeitfenster passt.',
  'I will come back to you with a time that fits your window.',
];

/** Which promise each intent's holding reply makes — mirrors the proxy's PROMISE_ANCHORS. */
const PROMISE_BY_CATEGORY: Record<string, { de: string; en: string }> = {
  scheduling_request: { de: PROMISES[0], en: PROMISES[1] },
  availability_window: { de: PROMISES[2], en: PROMISES[3] },
};

/**
 * What "no slots ticked" restores: the promise this draft originally made. When the server already
 * swapped the promise for times, the draft no longer contains one — then it is the intent's own
 * promise, in the draft's language.
 */
export function originalPromiseOf(draft: string, german: boolean, category?: string): string {
  const inText = PROMISES.find((promise) => draft.includes(promise));
  if (inText) return inText;
  const byCategory = category ? PROMISE_BY_CATEGORY[category] : undefined;
  if (byCategory) return german ? byCategory.de : byCategory.en;
  return german ? PROMISES[0] : PROMISES[1];
}

/**
 * The comparison form of a proposal block. Only `<br>` variants and space runs are normalised —
 * the browser re-serialises exactly those in what the mirror wrote, so comparing raw HTML would
 * read its own text back as "edited" and freeze the mirror. Every OTHER tag survives on purpose:
 * the mirror only ever writes text and `<br>`, so a `<strong>` or `<a>` inside the block can only
 * be the recruiter's formatting — and must therefore fail the equality and stop the mirror, not
 * be stripped out of the comparison and then overwritten.
 */
const textOf = (html: string) =>
  html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** What a proposal write hands back: the new editor content, plus the block to remember. */
export interface ProposalWrite {
  html: string;
  /** The block as written (null when the write restored the promise) — the mirror's provenance. */
  block: string | null;
}

/** The manual "✉️ Propose in reply": replace the current proposal (or the promise), else append. */
export function proposeInReply(current: string, slots: GradedSlot[], german: boolean): ProposalWrite {
  const block = proposalBlock(slots, german);
  const existing = existingProposal(current);
  if (existing) return { html: current.replace(existing, block), block };
  const promised = PROMISES.find((promise) => current.includes(promise));
  return { html: promised ? current.replace(promised, block) : `${current}<br><br>${block}`, block };
}

/**
 * The tick mirror: returns the adjusted reply, or null when nothing may be adjusted — see
 * ReplyTools.syncProposalSlots for the rules. `lastWritten` is the block the previous write put
 * in (from ProposalWrite.block): a block whose TEXT no longer matches it was edited by hand, and
 * the mirror keeps out — a recruiter's rewording must survive every later tick.
 */
export function syncProposalInReply(
  current: string,
  slots: GradedSlot[],
  german: boolean,
  originalPromise: string,
  lastWritten: string | null
): ProposalWrite | null {
  const existingRe = existingProposal(current);
  const existingText = existingRe ? (current.match(existingRe)?.[0] ?? null) : null;
  if (existingText !== null) {
    const untouched = lastWritten !== null && textOf(existingText) === textOf(lastWritten);
    if (!untouched) return null;
    if (slots.length === 0) {
      // Every tick removed → the reply goes back to promising times instead of listing none.
      return { html: current.replace(existingText, originalPromise), block: null };
    }
    const block = proposalBlock(slots, german);
    return { html: current.replace(existingText, block), block };
  }
  if (slots.length === 0) return null;
  const promised = PROMISES.find((promise) => current.includes(promise));
  if (!promised) return null;
  const block = proposalBlock(slots, german);
  return { html: current.replace(promised, block), block };
}
